import type { LessonLite, ModuleLite } from '@/types/content';

export type ModuleWithLessons = ModuleLite & { lessons: LessonLite[] };

export type LessonNavTarget = {
  lessonId: string;
  moduleSlug: string;
};

export type LessonPosition = {
  current: number;
  total: number;
};

function sortModules(modules: ModuleWithLessons[]): ModuleWithLessons[] {
  return [...modules].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function sortLessons(lessons: LessonLite[]): LessonLite[] {
  return [...lessons].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Lista plana de aulas na ordem do curso (módulos por `order`, aulas por `order`). */
export function flattenCourseLessons(modules: ModuleWithLessons[]): LessonNavTarget[] {
  const flat: LessonNavTarget[] = [];

  for (const module of sortModules(modules)) {
    if (!module.slug) continue;
    for (const lesson of sortLessons(module.lessons ?? [])) {
      flat.push({
        lessonId: String(lesson.id),
        moduleSlug: module.slug,
      });
    }
  }

  return flat;
}

export function getLessonPosition(
  modules: ModuleWithLessons[],
  currentLessonId: string | undefined
): LessonPosition | null {
  if (!currentLessonId) return null;

  const flat = flattenCourseLessons(modules);
  const index = flat.findIndex((item) => item.lessonId === String(currentLessonId));

  if (index < 0) return null;

  return {
    current: index + 1,
    total: flat.length,
  };
}

export function getAdjacentLesson(
  modules: ModuleWithLessons[],
  currentLessonId: string | undefined,
  direction: 'next' | 'prev'
): LessonNavTarget | null {
  if (!currentLessonId) return null;

  const flat = flattenCourseLessons(modules);
  const index = flat.findIndex((item) => item.lessonId === String(currentLessonId));

  if (index < 0) return null;

  const targetIndex = direction === 'next' ? index + 1 : index - 1;

  if (targetIndex < 0 || targetIndex >= flat.length) {
    return null;
  }

  return flat[targetIndex];
}
